import { useGetRolesQuery } from "api/TenantSlug/Role/hooks";
import { userApi } from "api/TenantSlug/User/api";
import { useFetchAllPaginatedData } from "hooks/useFetchAllPaginatedData";
import { useCallback, useMemo } from "react";
import { OfferAccess, RoleEnum } from "types/enums";

import OfferData = App.Data.OfferData;
import UserData = App.Data.UserData;

interface UseOfferUserListParams {
    offer?: OfferData | null;
    showUserSelectGuarded: boolean;
    canViewOwn: boolean;
    currentUser: UserData | null;
}

export function useOfferUserList({ offer, showUserSelectGuarded, canViewOwn, currentUser }: UseOfferUserListParams) {
    const isPublicOffer = offer?.access === OfferAccess.Public;

    const { data: rolesData, isLoading: isRolesLoading } = useGetRolesQuery(
        {},
        { enabled: isPublicOffer && showUserSelectGuarded }
    );
    const affiliateRoleId = rolesData?.roles.find((r) => r.name === RoleEnum.AffiliatePartner)?.id;

    const fetchAffiliates = useCallback(
        (payload: Parameters<typeof userApi.index>[0]) => {
            return userApi.index({
                ...payload,
                filter: {
                    filter: { "roles.id": affiliateRoleId },
                    sort: "nickname",
                },
            });
        },
        [affiliateRoleId]
    );

    const getAllAffiliates = useFetchAllPaginatedData(fetchAffiliates, {}, "users", {
        enabled: isPublicOffer && showUserSelectGuarded && !!affiliateRoleId,
    });
    const allAffiliates: UserData[] = getAllAffiliates.data ?? [];

    const users = useMemo(() => {
        if (isPublicOffer) {
            if (showUserSelectGuarded) {
                return allAffiliates;
            }

            return canViewOwn && currentUser ? [currentUser] : [];
        }

        const affiliates = offer?.affiliates ?? [];
        const otherAffiliates = currentUser ? affiliates.filter((a) => a.id !== currentUser.id) : [];

        const usersList: UserData[] = [];

        if (canViewOwn && currentUser) {
            usersList.push(currentUser);
        }

        if (showUserSelectGuarded) {
            usersList.push(...otherAffiliates);
        }

        return usersList;
    }, [isPublicOffer, allAffiliates, offer?.affiliates, currentUser, canViewOwn, showUserSelectGuarded]);

    const isLoading = isPublicOffer && showUserSelectGuarded && (isRolesLoading || getAllAffiliates.isLoading);

    const defaultUserId =
        canViewOwn && currentUser && users.some((user) => user.id === currentUser.id) ? currentUser.id : users[0]?.id;

    return {
        users,
        isLoading,
        defaultUserId,
    };
}
